import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Copy, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError, merchantApiKeys } from '@/api/client';
import type { ApiKeyCreated, RateLimitTier } from '@/api/types';
import { Empty } from '@/components/empty';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export const Route = createFileRoute('/m/api-keys')({
  component: MerchantApiKeys,
});

const TIERS: RateLimitTier[] = ['free', 'standard', 'premium', 'enterprise'];

function MerchantApiKeys() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['merchant-api-keys'],
    queryFn: merchantApiKeys.list,
  });
  const create = useMutation({
    mutationFn: (body: Parameters<typeof merchantApiKeys.create>[0]) =>
      merchantApiKeys.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['merchant-api-keys'] }),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => merchantApiKeys.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['merchant-api-keys'] }),
  });

  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState<ApiKeyCreated | null>(null);
  const [tier, setTier] = useState<RateLimitTier>('standard');
  const [allowText, setAllowText] = useState('');

  const onCreate = () => {
    const allowed = allowText
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    create.mutate(
      {
        scopes: ['payments:write', 'payments:read'],
        allowed_ips: allowed,
        rate_limit_tier: tier,
      },
      {
        onSuccess: (k) => {
          setSecret(k);
          setOpen(false);
          setAllowText('');
        },
        onError: (e) =>
          toast.error(e instanceof ApiError ? e.message : (t('api_keys.create_failed') as string)),
      },
    );
  };

  const onRevoke = (id: string) => {
    if (!confirm(t('api_keys.revoke_confirm') as string)) return;
    revoke.mutate(id, {
      onSuccess: () => toast.success(t('api_keys.revoked_toast') as string),
      onError: (e) =>
        toast.error(e instanceof ApiError ? e.message : (t('api_keys.revoke_failed') as string)),
    });
  };

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('api_keys.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('api_keys.description')}</p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" /> {t('api_keys.new')}
        </Button>
      </header>

      <div className="rounded-md border border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('api_keys.cols.key_id')}</TableHead>
              <TableHead>{t('api_keys.cols.tier')}</TableHead>
              <TableHead>{t('api_keys.cols.allowlist')}</TableHead>
              <TableHead>{t('api_keys.cols.status')}</TableHead>
              <TableHead>{t('api_keys.cols.last_used')}</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data?.map((k) => (
              <TableRow key={k.id}>
                <TableCell className="font-mono text-xs">{k.key_id}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="capitalize">
                    {k.rate_limit_tier}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {k.allowed_ips?.length ? k.allowed_ips.join(', ') : t('api_keys.any_ip')}
                </TableCell>
                <TableCell>
                  <Badge variant={k.status === 'active' ? 'success' : 'outline'}>{k.status}</Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {k.last_used_at ? dayjs(k.last_used_at).format('YYYY-MM-DD HH:mm') : '—'}
                </TableCell>
                <TableCell className="text-right">
                  {k.status === 'active' && (
                    <Button variant="outline" size="sm" onClick={() => onRevoke(k.id)}>
                      {t('common.revoke')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!list.isLoading && list.data?.length === 0 && (
          <Empty title={t('api_keys.none')} className="border-0" />
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('api_keys.create_title')}</DialogTitle>
            <DialogDescription>{t('api_keys.create_description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('api_keys.allowlist_label')}</Label>
              <textarea
                className="mt-1 w-full rounded-md border border-input bg-background p-2 font-mono text-xs"
                rows={3}
                value={allowText}
                onChange={(e) => setAllowText(e.target.value)}
              />
            </div>
            <div>
              <Label>{t('api_keys.tier_label')}</Label>
              <Select value={tier} onValueChange={(v) => setTier(v as RateLimitTier)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIERS.map((tv) => (
                    <SelectItem key={tv} value={tv} className="capitalize">
                      {t(`api_keys.tiers.${tv}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={onCreate} disabled={create.isPending}>
              {t('common.create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!secret} onOpenChange={(o) => !o && setSecret(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('api_keys.secret_revealed_title')}</DialogTitle>
            <DialogDescription>{t('api_keys.secret_revealed_description')}</DialogDescription>
          </DialogHeader>
          {secret && (
            <div className="space-y-3">
              <div className="rounded-md border border-border bg-muted/30 p-3">
                <code className="text-xs break-all">{secret.secret}</code>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(secret.secret);
                  toast.success(t('common.copied') as string);
                }}
              >
                <Copy className="h-4 w-4" /> {t('common.copy')}
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setSecret(null)}>{t('api_keys.secret_revealed_ack')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
